/**
 * SubscriptionsPage - Redesigned with Quiz styling
 */

import { useMemo } from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import type { RechargeSubscription } from "../types";
import {
  buildRecipeGridForBundle,
  buildTreatItemsFromBundle,
  buildVariantIndex,
  isBundleSub,
  parseBundlePlan,
  parseLegacySubscription,
  buildLegacyMealItems,
  estimateDeliveryDate,
  type BundlePlan,
  type LegacySubscription,
} from "../utils/recharge";
import { BundlePlanCard } from "../components/BundlePlanCard";
import { LegacySubscriptionCard } from "../components/LegacySubscriptionCard";
import { DeliveryScheduleCard } from "../components/DeliveryScheduleCard";
import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { PageTitle } from "../components/shared/PageTitle";
import { QuizButton } from "../components/shared/QuizButton";
import { QUIZ } from "@/theme/quizTokens";

export function SubscriptionsPage({
  hasSubs,
  subs,
  upcoming,
  orders,
  addresses,
  products,
  cutoffHours = 36,
  deliveryDayNames = [],
  timezone = "Europe/London",
  canResume = false,
  pausedSubs = [],
  onResume,
  onEditMeals,
  onSkipDelivery,
  onChangeDeliveryDate,
  onChangeFrequency,
  onChangeDailyServing,
  onChangeQuantity,
  onStopPlan,
}: {
  hasSubs: boolean;
  subs: RechargeSubscription[];
  upcoming?: any[];
  orders?: any[];
  addresses?: any[];
  products: any[];
  cutoffHours?: number;
  deliveryDayNames?: string[];
  timezone?: string;
  canResume?: boolean;
  /** Cancelled bundle subs shown behind a frosted "resume" overlay when nothing is active */
  pausedSubs?: RechargeSubscription[];
  onResume?: () => void;
  onEditMeals: (plan: BundlePlan | LegacySubscription) => void;
  onSkipDelivery?: (subscriptionId: string) => void;
  onChangeDeliveryDate?: (subscriptionId: string) => void;
  onChangeFrequency?: (subscriptionId: string) => void;
  onChangeDailyServing?: (subscriptionId: string) => void;
  onChangeQuantity?: (subscriptionId: string) => void;
  onStopPlan?: (subscriptionId: string) => void;
}) {
  const variantIndex = useMemo(
    () => buildVariantIndex(products || []),
    [products]
  );

  // Build address map from Recharge addresses (includes order_attributes with delivery dates)
  const addressMap = useMemo(() => {
    const map = new Map<string, {
      firstName?: string;
      lastName?: string;
      address1?: string;
      address2?: string;
      city?: string;
      province?: string;
      zip?: string;
      country?: string;
    }>();

    // Populate from addresses (shipping info only — delivery dates derived from charge date)
    for (const addr of (addresses || [])) {
      const addressId = String(addr.id || "");
      if (!addressId) continue;

      map.set(addressId, {
        firstName: addr.first_name,
        lastName: addr.last_name,
        address1: addr.address1,
        address2: addr.address2,
        city: addr.city,
        province: addr.province,
        zip: addr.zip,
        country: addr.country,
      });
    }

    // Fallback: also check upcoming charges for shipping addresses (if not in addresses)
    for (const charge of (upcoming || [])) {
      const addressId = String(charge.address_id || "");
      const shippingAddress = charge.shipping_address;

      if (addressId && shippingAddress && !map.has(addressId)) {
        map.set(addressId, {
          firstName: shippingAddress.first_name,
          lastName: shippingAddress.last_name,
          address1: shippingAddress.address1,
          address2: shippingAddress.address2,
          city: shippingAddress.city,
          province: shippingAddress.province,
          zip: shippingAddress.zip,
          country: shippingAddress.country,
        });
      }
    }

    return map;
  }, [addresses, upcoming]);

  // With no active subs, render the paused plan(s) behind a frosted resume overlay
  const isPausedView = subs.length === 0 && pausedSubs.length > 0;
  const effectiveSubs = isPausedView ? pausedSubs : subs;

  // Group all subscriptions by address_id
  const subscriptionsByAddress = useMemo(() => {
    const groups: Record<string, {
      addressId: string;
      bundleSubs: any[];
      otherSubs: any[];
      allSubs: any[];
      anchorSub: any;
      nextChargeDate: string | null;
      nextDeliveryDate: string | null;
      isCharged: boolean;
      address: {
        firstName?: string;
        lastName?: string;
        address1?: string;
        address2?: string;
        city?: string;
        province?: string;
        zip?: string;
        country?: string;
      } | null;
    }> = {};

    for (const sub of effectiveSubs) {
      const addressId = String((sub as any).address_id || "unknown");

      if (!groups[addressId]) {
        groups[addressId] = {
          addressId,
          bundleSubs: [],
          otherSubs: [],
          allSubs: [],
          anchorSub: null,
          nextChargeDate: null,
          nextDeliveryDate: null,
          isCharged: false,
          address: addressMap.get(addressId) || null,
        };
      }

      groups[addressId].allSubs.push(sub);

      if (isBundleSub(sub)) {
        groups[addressId].bundleSubs.push(sub);
      } else {
        // Check if this is a treat subscription - skip treats (managed on Extras page)
        const variantId = String((sub as any).shopify_variant_id || (sub as any).external_variant_id || "");
        const info = variantIndex.get(variantId);
        if (!info?.tags?.includes("Treats")) {
          groups[addressId].otherSubs.push(sub);
        }
      }
    }

    // Calculate dates for each address group
    for (const group of Object.values(groups)) {
      // Use first subscription as anchor
      group.anchorSub = group.allSubs[0];
      group.nextChargeDate = group.anchorSub?.next_charge_scheduled_at || null;

      // Derive delivery date from charge date: charge + cutoff hours → next available day
      if (group.nextChargeDate) {
        group.nextDeliveryDate = estimateDeliveryDate(group.nextChargeDate, cutoffHours, deliveryDayNames);
      }

      // Disable buttons if delivery date is within the cutoff window (e.g. 36 hours before delivery)
      if (group.nextDeliveryDate) {
        try {
          // Delivery date at midnight in the shop's timezone
          const deliveryMidnight = new Date(group.nextDeliveryDate + "T00:00:00");
          const now = new Date();
          // Cutoff deadline = delivery midnight - cutoffHours
          const cutoffDeadlineMs = deliveryMidnight.getTime() - (cutoffHours * 60 * 60 * 1000);
          group.isCharged = now.getTime() >= cutoffDeadlineMs;
        } catch {
          group.isCharged = false;
        }
      } else {
        group.isCharged = false;
      }
    }

    return Object.values(groups).filter(g => g.bundleSubs.length > 0 || g.otherSubs.length > 0);
  }, [effectiveSubs, variantIndex, addressMap, cutoffHours, deliveryDayNames]);

  const hasActiveSubscriptions = subscriptionsByAddress.length > 0;

  return (
    <Stack spacing={{ xs: 2, md: 3 }}>
      {/* Header */}
      <QuizStyledCard>
        <PageTitle
          title="Your Subscriptions"
          subtitle="Manage your meal plans and delivery schedule"
        />

        {!hasSubs && (
          <Box sx={{ mt: 3 }}>
            <Skeleton height={36} sx={{ borderRadius: 2 }} />
            <Skeleton height={36} sx={{ borderRadius: 2, mt: 1 }} />
            <Skeleton height={120} sx={{ borderRadius: 2, mt: 1 }} />
          </Box>
        )}
      </QuizStyledCard>

      {hasSubs && !hasActiveSubscriptions && (
        <QuizStyledCard variant="white">
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 4, md: 6 },
            }}
          >
            <Box
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                borderRadius: "50%",
                backgroundColor: QUIZ.color.cream2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <SubscriptionsIcon
                sx={{ fontSize: { xs: 40, md: 50 }, color: QUIZ.color.brown }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 22 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                mb: 1,
              }}
            >
              No active subscriptions
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: QUIZ.color.brown,
                opacity: 0.75,
                mb: 3,
              }}
            >
              {canResume
                ? "Your plan is paused. Resume it to restart your deliveries"
                : "Start the quiz to create a personalized meal plan for your dog"}
            </Typography>
            {canResume && onResume ? (
              <QuizButton variant="primary" onClick={onResume}>
                Resume my plan
              </QuizButton>
            ) : (
              <QuizButton variant="primary" href="/pages/quiz-1">
                Start the Quiz
              </QuizButton>
            )}
          </Box>
        </QuizStyledCard>
      )}

      {/* Subscriptions grouped by address; paused plans render behind a frosted resume overlay */}
      <Box sx={{ position: "relative" }}>
        <Stack
          spacing={{ xs: 2, md: 3 }}
          sx={isPausedView ? { pointerEvents: "none", userSelect: "none", filter: "saturate(0.9)" } : undefined}
          aria-hidden={isPausedView || undefined}
        >
      {subscriptionsByAddress.map((group) => {
        const anchorSubscriptionId = group.anchorSub ? String(group.anchorSub.id) : null;

        return (
          <Stack key={group.addressId} spacing={{ xs: 2, md: 3 }}>
            {/* Delivery Schedule Card for this address */}
            {anchorSubscriptionId && (
              <DeliveryScheduleCard
                nextDeliveryDate={group.nextDeliveryDate}
                nextChargeDate={group.nextChargeDate}
                isCharged={group.isCharged}
                cutoffHours={cutoffHours}
                address={group.address}
                onChangeDeliveryDate={
                  onChangeDeliveryDate && !group.isCharged
                    ? () => onChangeDeliveryDate(anchorSubscriptionId)
                    : undefined
                }
                onSkipDelivery={
                  onSkipDelivery && !group.isCharged
                    ? () => onSkipDelivery(anchorSubscriptionId)
                    : undefined
                }
              />
            )}

            {/* Bundle plans in this address */}
            {group.bundleSubs.map((sub: any) => {
              const plan = parseBundlePlan(sub);
              const boxInfo = plan.boxVariantId
                ? variantIndex.get(String(plan.boxVariantId)) || null
                : null;

              const recipes = buildRecipeGridForBundle(plan, variantIndex);
              const treats = buildTreatItemsFromBundle(plan, variantIndex);

              return (
                <BundlePlanCard
                  key={String(sub.id)}
                  sub={sub}
                  plan={plan}
                  boxInfo={boxInfo}
                  recipes={recipes}
                  treats={treats}
                  onEditMeals={() => onEditMeals(plan)}
                  onChangeRecipes={() => onEditMeals(plan)}
                  onChangeFrequency={
                    onChangeFrequency
                      ? () => onChangeFrequency(plan.subscriptionId)
                      : undefined
                  }
                  onChangeDailyServing={
                    onChangeDailyServing
                      ? () => onChangeDailyServing(plan.subscriptionId)
                      : undefined
                  }
                  onStopPlan={
                    onStopPlan
                      ? () => onStopPlan(plan.subscriptionId)
                      : undefined
                  }
                />
              );
            })}

            {/* Legacy subscriptions in this address */}
            {group.otherSubs.map((sub: any) => {
              const legacy = parseLegacySubscription(sub);
              const productInfo = legacy.variantId
                ? variantIndex.get(String(legacy.variantId)) || null
                : null;

              const meals = buildLegacyMealItems(legacy, variantIndex);

              return (
                <LegacySubscriptionCard
                  key={String(sub.id)}
                  sub={sub}
                  legacy={legacy}
                  productInfo={productInfo}
                  meals={meals}
                  onEditMeals={() => onEditMeals(legacy)}
                  onChangeQuantity={
                    onChangeQuantity
                      ? () => onChangeQuantity(legacy.subscriptionId)
                      : undefined
                  }
                  onStopPlan={
                    onStopPlan
                      ? () => onStopPlan(legacy.subscriptionId)
                      : undefined
                  }
                />
              );
            })}
          </Stack>
        );
      })}
        </Stack>

        {/* Frosted glass resume overlay over the paused plan */}
        {isPausedView && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              borderRadius: "20px",
              background: "rgba(255, 253, 248, 0.55)",
              backdropFilter: "blur(14px) saturate(150%)",
              WebkitBackdropFilter: "blur(14px) saturate(150%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: QUIZ.font.display,
                fontSize: { xs: 26, md: 32 },
                color: QUIZ.color.ink,
                textTransform: "uppercase",
                lineHeight: 1.1,
              }}
            >
              Your plan is paused
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 14, md: 15 },
                fontWeight: 600,
                color: QUIZ.color.grey,
                maxWidth: 420,
              }}
            >
              Everything is saved just as you left it. Resume to restart your deliveries.
            </Typography>
            {onResume && (
              <QuizButton variant="primary" onClick={onResume} sx={{ mt: 1 }}>
                Resume my plan
              </QuizButton>
            )}
          </Box>
        )}
      </Box>
    </Stack>
  );
}
